import {
  EnergyHistoricalDataFeaturesGroup,
  TrendingEnumMappedProperties,
  TrendingEnumSeriesData,
} from '@store';
import { Unit } from '../../../../app/shared/common/units';

import { SUFFIX } from '../../../../app/shared/common/units';
import { FeatureNames } from '../operational-data/operational-data.constants';
import { Observable } from 'rxjs';

export const powerChartFeatures: EnergyHistoricalDataFeaturesGroup[] = [
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_ENERGY_BALANCE',
    features: [
      { feature: FeatureNames.energyProductionTotal },
      { feature: FeatureNames.energyConsumptionTotal },
      { feature: FeatureNames.emsEnergyProductionToConsumption },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_GRID',
    features: [
      { feature: FeatureNames.energyGridFeedIn },
      { feature: FeatureNames.energyGridConsumption },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_BATTERY',
    features: [
      { feature: FeatureNames.energyBatteryCharge },
      { feature: FeatureNames.energyBatteryDischarge },
      { feature: FeatureNames.energyBatteryStateOfCharge, unit: SUFFIX.percentage },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_AUTONOMY',
    features: [
      { feature: FeatureNames.kpiAutarky, unit: SUFFIX.percentage },
      { feature: FeatureNames.kpiSelfConsumption, unit: SUFFIX.percentage },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_CONSUMER',
    features: [
      { feature: FeatureNames.energyHeatPumpConsumption },
      { feature: FeatureNames.energyHouseholdConsumption },
      { feature: FeatureNames.emsEnergyConsumptionVcs },
      { feature: FeatureNames.emsEnergyConsumptionPvImmersionHeater },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_ENVIRONMENTAL',
    features: [
      { feature: FeatureNames.emsCarbonSavings, unit: SUFFIX.kilogram },
      { feature: FeatureNames.emsCarbonEmissions, unit: SUFFIX.kilogram },
    ],
  },
];

export const thermalChartFeatures: EnergyHistoricalDataFeaturesGroup[] = [
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_ENERGY_CONSUMPTION',
    features: [{ feature: FeatureNames.heatPumpConsumption }],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_PER_DEVICE',
    features: [
      { feature: FeatureNames.compressorConsumption },
      { feature: FeatureNames.electricHeaterConsumption },
    ],
  },
  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_PER_USAGE',
    features: [
      { feature: FeatureNames.heatingConsumption },
      { feature: FeatureNames.hotWaterConsumption },
      { feature: FeatureNames.coolingConsumption },
    ],
  },

  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_THERMAL_OUTPUT',
    features: [{ feature: FeatureNames.thermalOutputTotal }],
  },

  {
    groupKey: '+HEMS.PARAMETERS_POPUP.GROUP_THERMAL_OUTPUT',
    features: [
      { feature: FeatureNames.heatingOutput },
      { feature: FeatureNames.hotWaterOutput },
      { feature: FeatureNames.coolingOutput },
    ],
  },
];

export enum HemsDataTypeEnum {
  ENERGY = 'energy',
  RELATIVE = 'relative',
}

export type TimeResolution = '1m' | '5m' | '1h' | '15m' | '1d' | '1M' | '1y' | '1Y' | '1W';

export type AmountResolution = '30';

export interface HemsPayload {
  properties?: HemsProperties[];
  version?: string;
  resolution?: TimeResolution;
  gateway_id: string;
  boiler_id?: string;
  timeframe?: string;
  start_datetime?: string;
  end_datetime?: string;
  device_id?: string;
}

export enum HemsUnit {
  KWh = 'kWh',
  KW = 'kW',
  Percentage = 'percentage',
  Kkg = 'kg',
  Pc = 'pc.',
  CubicMeter = 'cubicMeter',
  Celcius = 'celcius',
}

export const hemsUnitToCommonUnitMapping = {
  kWh: Unit.KilowattHour,
  kW: Unit.Kilowatt,
  percentage: Unit.Percent,
  kg: Unit.Kilogram,
  'pc.': Unit.PC,
  cubicMeter: Unit.CubicMeter,
  celcius: Unit.Celsius,
};

export type HemsProperties =
  | typeof FeatureNames.kpiAutarky
  | typeof FeatureNames.kpiSelfConsumption
  | typeof FeatureNames.energyProductionTotal
  | typeof FeatureNames.energyConsumptionTotal
  | typeof FeatureNames.energyHeatPumpConsumption
  | typeof FeatureNames.energyHouseholdConsumption
  | typeof FeatureNames.emsCarbonSavings
  | typeof FeatureNames.emsCarbonEmissions
  | typeof FeatureNames.energyGridConsumption
  | typeof FeatureNames.energyGridFeedIn
  | typeof FeatureNames.energyBatteryStateOfCharge
  | typeof FeatureNames.energyBatteryCharge
  | typeof FeatureNames.energyBatteryDischarge
  | typeof FeatureNames.heatPumpConsumption
  | typeof FeatureNames.compressorConsumption
  | typeof FeatureNames.electricHeaterConsumption
  | typeof FeatureNames.heatingConsumption
  | typeof FeatureNames.hotWaterConsumption
  | typeof FeatureNames.coolingConsumption
  | typeof FeatureNames.thermalOutputTotal
  | typeof FeatureNames.heatingOutput
  | typeof FeatureNames.hotWaterOutput
  | typeof FeatureNames.coolingOutput
  | typeof FeatureNames.emsEnergyConsumptionVcs
  | typeof FeatureNames.emsEnergyConsumptionPvImmersionHeater
  | typeof FeatureNames.legacyHeatPumpConsumption
  | typeof FeatureNames.emsEnergyProductionToConsumption
  | typeof FeatureNames.legacyBoilerFuelConsumption;

export interface RawHemsHistoricalData {
  gateway_id: string;
  device_id: string;
  resolution: string;
  timeframe: string;
  properties: string[];
  summary_properties: {
    [key in HemsProperties]: HemsUnit;
  };
  timeseries_properties: {
    [key in HemsProperties]: HemsUnit;
  };
  data: {
    summary: {
      [key: string]: number;
    };
    timeseries: {
      [key: string]: number | string;
      timestamp: string;
    }[];
  };
}

export interface RawTrendingData {
  datalength: number;
  device_id: string[];
  enum_properties: TrendingEnumMappedProperties;
  enum_series: TrendingEnumSeriesData[];
  gateway_id: string;
  point_interval: number;
  point_start: number;
  point_end: number;
  resolution: TimeResolution;
  series: RawTrendingDataSeries[];
}

export interface RefrigerationCircuitMessagesData {
  gateway_id: string;
  device_pair_id: string;
  data: RefrigerationCircuitMessagesDataDetails[];
}

export interface RefrigerationCircuitMessagesDataDetails {
  property: string;
  timestamp: string;
  error_code: string;
  status: string;
  count: number;
  priority: string;
}

export interface RawTrendingDataSeries {
  feature: string;
  datalength?: number;
  point_interval?: number;
  device_id?: string;
  data: number[];
}

export const trendingProperties = [
  'heating.sensors.volumetricFlow.allengra/value',
  'heating.boiler.temperature/value',
  'fuelCell.sensors.temperature.supply/value',
  'fuelCell.sensors.temperature.return/value',
  'heating.fuelCell.sensors.temperature.return/value',
  'heating.sensors.temperature.return/value',
  'heating.secondaryHeatGenerator.status/value',
  'heating.buffer.sensors.temperature.main/value',
  'heating.burners.0.modulation/value',
  'heating.power.production.current/value',
  'fuelCell.statistics/operationHours',
  'fuelCell.operating.phase/value',
  'fuelCell.operating.modes.active/value',
  'fuelCell.statistics/productionHours',
  'gateway.connected',
  'heating.circuits.0.sensors.temperature.supply/value',
  'heating.circuits.1.sensors.temperature.supply/value',
  'heating.circuits.2.sensors.temperature.supply/value',
  'heating.circuits.3.sensors.temperature.supply/value',
  'heating.circuits.0.temperature/value',
  'heating.circuits.1.temperature/value',
  'heating.circuits.2.temperature/value',
  'heating.circuits.3.temperature/value',
  'heating.circuits.0.heating.curve/shiftValue',
  'heating.circuits.1.heating.curve/shiftValue',
  'heating.circuits.2.heating.curve/shiftValue',
  'heating.circuits.3.heating.curve/shiftValue',
  'heating.circuits.0.heating.curve/slopeValue',
  'heating.circuits.1.heating.curve/slopeValue',
  'heating.circuits.2.heating.curve/slopeValue',
  'heating.circuits.3.heating.curve/slopeValue',
  'heating.circuits.0.operating.programs.normal/temperatureValue',
  'heating.circuits.1.operating.programs.normal/temperatureValue',
  'heating.circuits.2.operating.programs.normal/temperatureValue',
  'heating.circuits.3.operating.programs.normal/temperatureValue',
  'heating.circuits.0.operating.programs.reduced/temperatureValue',
  'heating.circuits.1.operating.programs.reduced/temperatureValue',
  'heating.circuits.2.operating.programs.reduced/temperatureValue',
  'heating.circuits.3.operating.programs.reduced/temperatureValue',
  'heating.circuits.0.operating.programs.comfort/temperatureValue',
  'heating.circuits.1.operating.programs.comfort/temperatureValue',
  'heating.circuits.2.operating.programs.comfort/temperatureValue',
  'heating.circuits.3.operating.programs.comfort/temperatureValue',
  'heating.compressors.0.sensors.temperature.inlet/value',
  'heating.compressors.0.sensors.temperature.motorChamber/value',
  'heating.compressors.0.sensors.temperature.oil/value',
  'heating.compressors.0.sensors.temperature.outlet/value',
  'heating.economizers.0.sensors.temperature.liquid/value',
  'heating.evaporators.0.sensors.temperature.liquid/value',
  'heating.primaryCircuit.sensors.temperature.supply/value',
  'heating.compressors.0.speed.current/value',
  'heating.sensors.valve.0.expansion/value',
  'heating.sensors.valve.1.expansion/value',
  'heating.primaryCircuit.fans.0.current/value',
  'heating.primaryCircuit.fans.1.current/value',
  'heating.compressors.0.sensors.pressure.inlet/value',
  'heating.compressors.0.sensors.pressure.outlet/value',
  'heating.secondaryCircuit.sensors.temperature.supply/value',
  'heating.sensors.temperature.allengra/value',
  'heating.sensors.temperature.outside/value',
  'heating.secondaryCircuit.valves.fourThreeWay/current',
  'heating.sensors.pressure.supply/value',
  'fuelCell.statistics/productionHoursPerDay',
  'heating.solar.sensors.temperature.dhw/value',
  'heating.solar.sensors.temperature.collector/value',
  'heating.burner.preConditions/status',
  'fuelCell.operating.modes.active/value',
  'fuelCell.operating.phase/value',
  'heating.heatingRod.status/level1',
  'heating.heatingRod.status/level2',
  'heating.heatingRod.status/level3',
  'heating.primaryCircuit.valves.fourWay/state',
  'fuelCell.electricalEnergyConsumption.value/value',
  'fuelCell.managers.energy.prediction.power.consumption/value',
  'fuelCell.managers.energy.prediction.runtime/value',
  'fuelCell.managers.energy.timeTillNextStart/value',
  'fuelCell.prediction.heating.deficit/value',
  'heating.dhw.sensors.temperature.hotWaterStorage',
  'heating.dhw.sensors.temperature.hotWaterStorage/value',
  'heating.dhw.sensors.temperature.hotWaterStorage.midBottom/value',
  'heating.dhw.sensors.temperature.hotWaterStorage.middle/value',
  'heating.dhw.sensors.temperature.hotWaterStorage.bottom/value',
  'heating.dhw.sensors.temperature.hotWaterStorage.top/value',
  'heating.boiler.sensors.temperature.commonReturn/value',
  'heating.sensors.valve.bufferDischargeThreeWayValve/value',
  'fuelCell.managers.energy/historyReset',
  'heating.heat.production.current/value',
  'heating.boiler.sensors.temperature.commonSupply/value',
  'heating.sensors.temperature.systemReturn/value',
  'heating.sensors.temperature.hydraulicSeparator/value',
  'heating.dhw.pumps.primary/status',
  'heating.dhw.pumps.circulation/status',
  'heating.boiler.pumps.internal/status',
  'heating.circuits.0.circulation.pump/status',
  'heating.circuits.1.circulation.pump/status',
  'heating.circuits.2.circulation.pump/status',
  'heating.circuits.3.circulation.pump/status',
];

export const communicationModuleProperties = ['gateway.wifi/strength', 'gateway.status/online'];

export const energyStorageProperties = [
  'dl.ess.inverter.ac.power.consolidated/total',
  'dl.ess.power.consolidated/total',
  'dl.ess.stateOfCharge.consolidated/total',
  'dl.photovoltaic.string.voltage/stringOne',
  'dl.photovoltaic.string.voltage/stringTwo',
  'dl.photovoltaic.string.voltage/stringThree',
  'dl.ess.sensors.temperature.ambient/value',
  'dl.ess.stateOfCharge/value',
  'dl.ess.inverter.ac.power/activePower',
  'dl.ess.power/value',
];

export const energyStorageTotalPropertiesForIndividualVitocharge = [
  'dl.ess.stateOfCharge/value',
  'dl.ess.inverter.ac.power/activePower',
  'dl.ess.power/value',
];

export const energyStorageTotalPropertiesForMultipleVitocharge = [
  'dl.ess.inverter.ac.power.consolidated/total',
  'dl.ess.power.consolidated/total',
  'dl.ess.stateOfCharge.consolidated/total',
];

export type TrendingProperties = typeof trendingProperties[number];
export interface DataTrendingRequestBody {
  gateway_id: string;
  device_id: string[];
  resolution: TimeResolution;
  start_datetime: Date;
  end_datetime: Date;
  properties: TrendingProperties[];
}

export interface HemsFeaturesPresentSincePayload {
  gateway_id: string;
  device_id?: string;
  properties?: string[];
  scope?: string;
}

export interface HemsFeaturesPresentSinceResponse {
  gateway_id: string;
  device_id: string;
  properties: PropertySinceFeature[];
}

export interface PropertySincePayload {
  gateway_id: string;
  device_id?: string;
}

export interface PropertySinceFeature {
  property: string;
  start_datetime: string;
}
export interface PropertySinceResponse {
  gateway_id: string;
  device_id: string;
  properties: PropertySinceFeature[];
}

export interface RefrigerationCircuitMessagesPayload {
  gateway_id: string;
  device_id?: string;
}

export enum OptimizationRecommendationsType {
  dhwSchedule = 'dhwSchedule',
  hc0temperature = 'hc0temperature',
  hc1temperature = 'hc1temperature',
  hc2temperature = 'hc2temperature',
  hc3temperature = 'hc3temperature',
  hc4temperature = 'hc4temperature',
  hc5temperature = 'hc5temperature',
  hc6temperature = 'hc6temperature',
  hc0heatingcurve = 'hc0heatingcurve',
  hc1heatingcurve = 'hc1heatingcurve',
  hc2heatingcurve = 'hc2heatingcurve',
  hc3heatingcurve = 'hc3heatingcurve',
  hc4heatingcurve = 'hc4heatingcurve',
  hc5heatingcurve = 'hc5heatingcurve',
  hc6heatingcurve = 'hc6heatingcurve',
}

export interface GenericDataResponse<T> {
  data: T;
}

export interface ViguideRecommendations {
  boilerSerial?: string;
  gatewayId?: string;
  installationId: number;
  recommendations: ViguideRecommendationData[];
}

export interface ViguideRecommendationData {
  group: 'factorySettings'; //  so far group always returns 'factorySettings' maybe it'll need to be updated in the future
  name: OptimizationRecommendationsType;
  syncDate: Date;
}

export interface RefrigerationCircuitMessagesQueryParams {
  resolution: AmountResolution;
}

export interface FeaturesHelperFormat {
  data: SeriesDataFormat;
  seriesName: string;
  visible: boolean;
}

export type SeriesDataFormat = [number | string, number][];

export interface HemsFlattenedFeatures {
  feature: HemsProperties;
  unit?: SUFFIX.percentage | SUFFIX.kilogram | SUFFIX.kilowattHour;
}

export interface AnalyticsApiModel {
  getDeviceAvailableProperties(
    body: PropertySincePayload
  ): Observable<GenericDataResponse<PropertySinceResponse>>;

  // getHemsPowerData(body: HemsPayload): Observable<{ data: RawHemsHistoricalData }>;

  // getHemsThermalData(body: HemsPayload): Observable<{ data: RawHemsHistoricalData }>;

  // getTrendingData(payload: DataTrendingRequestBody): Observable<{ data: RawTrendingData[] }>;

  // getRefrigerationCircuitMessages(
  //   payload: RefrigerationCircuitMessagesPayload
  // ): Observable<{ data: RefrigerationCircuitMessagesData[] }>;

  // getOptimizationRecommendations(): Observable<GenericDataResponse<ViguideRecommendations[]>>;

  // getDeviceAvailableProperties(
  //   body: PropertySincePayload
  // ): Observable<GenericDataResponse<PropertySinceResponse>>;

  getConnectivityRanking(companyId: string): Observable<GenericDataResponse<ConnectivityRanking>>;
}

export interface ConnectivityRanking {
  customer_id: string;
  lead_sales_org: string;
  sales_org_ranking: string;
  total_connected_installations: string;
  mocked_all_ranking_participants: string; // mocked property not returned by backend
  quarterly_overview: [
    {
      quarter: string;
      previous_quarter: string;
      new_installations: number;
      new_installations_relative_increase: string;
    }
  ];
}
